'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { PortalObjectKind } from '@terra/shared-dto-chat-types';
import type { CardRenderContext } from '../../tool-config-types';
import {
  PortalObjectReferenceCard,
  type PortalObjectType,
} from '../shared/PortalObjectReferenceCard';
import { ToolInlineStatusLine } from '../shared/ToolInlineStatusLine';
import {
  portalObjectKindToCardType,
  portalObjectPresentationToNavigationTarget,
  readPortalObjectPresentation,
} from '../../../utils/portal-object-presentation';

function assertNever(value: never): never {
  throw new Error(`Unhandled portal object kind: ${String(value)}`);
}

type PortalObjectPresentationCardProps = {
  ctx: CardRenderContext;
};

function resolveOpenedPrefix({
  objectKind,
  t,
}: {
  objectKind: PortalObjectKind;
  t: (key: string) => string;
}): string {
  switch (objectKind) {
    case 'Finding':
      return t('openedFinding');
    case 'Signal':
      return t('openedSignal');
    case 'EndPoint':
      return t('openedEndpoint');
    case 'WebPage':
      return t('openedWebpage');
    case 'Trace':
      return t('openedExecution');
    case 'SkillDraft':
      return t('openedSkillDraft');
    default:
      return assertNever(objectKind);
  }
}

function PortalObjectPresentationCard({
  ctx,
}: PortalObjectPresentationCardProps) {
  const t = useTranslations('designSystem.chat.objectReference');
  const presentation = readPortalObjectPresentation(
    ctx.toolCallData.output,
    ctx.toolCallData.input,
  );
  const navigationTarget = presentation
    ? portalObjectPresentationToNavigationTarget(presentation)
    : null;
  const status = ctx.toolCallData.status;
  const isRunning = status === 'started' || status === 'in-progress';
  const isError = status === 'error' || Boolean(ctx.toolCallData.error);
  const canShowObjectReference =
    status === 'completed' && Boolean(navigationTarget && presentation);

  if (isRunning) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={ctx.runningText ?? ctx.labelPresent}
      />
    );
  }

  if (isError) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={ctx.toolCallData.error ?? ctx.labelPast}
        isError
      />
    );
  }

  if (!canShowObjectReference || !navigationTarget || !presentation) {
    return null;
  }

  const objectType: PortalObjectType = portalObjectKindToCardType(
    presentation.objectKind,
  );

  return (
    <PortalObjectReferenceCard
      objectType={objectType}
      objectId={presentation.objectId}
      title={presentation.title}
      prefix={resolveOpenedPrefix({ objectKind: presentation.objectKind, t })}
      onOpenObject={ctx.onOpenObject}
      navigationTarget={navigationTarget}
    />
  );
}

export const renderPortalObjectPresentationCard = (
  ctx: CardRenderContext,
): ReactNode => <PortalObjectPresentationCard ctx={ctx} />;
